import React, { useEffect, useState, useContext, useMemo } from "react";
import { BarChart } from '@mui/x-charts/BarChart';
import { ReviewsContext } from '../context/ReviewsContext';
import { BusinessContext } from '../context/BusinessContext';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { formatChartValue } from '../utils/formatUtils';
import { 
  StandardChartCard,
  useResponsiveChartProps,
  getXAxisConfig,
  getYAxisConfig,
  CustomChartTooltip
} from './common/ChartDefaults';
import { useMediaQuery, useTheme, Box, CircularProgress, Typography } from '@mui/material';
import { colors } from '../utils/themeUtils';

/**
 * This component displays impact over time based on selected view (Sales or Rating)
 * It now fetches pre-aggregated data from the backend.
 */
const ImpactOverTimeChart = () => {
  const {
    aggregatedData,
    isAggregatedLoading,
    isReviewsLoading,
    isAnalysisLoading,
    selectedCategory,
    selectedSubject,
    selectedAttribute,
    view,
    setSelectedDate 
  } = useContext(ReviewsContext);

  const { currencySymbol, selectedBusinesses } = useContext(BusinessContext);
  
  const isSingleBusinessSelected = selectedBusinesses && selectedBusinesses.length === 1;
  const singleBusiness = isSingleBusinessSelected ? selectedBusinesses[0] : null;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { chartSx, slotProps: responsiveSlotProps, margin } = useResponsiveChartProps();

  const { categories, positiveData, negativeData, effectiveView } = useMemo(() => {
    if (isAggregatedLoading || !aggregatedData || !aggregatedData.impactOverTimeData) {
      return { categories: [], positiveData: [], negativeData: [], effectiveView: view || "Rating" }; 
    }

    const timeData = aggregatedData.impactOverTimeData;
    const currentEffectiveView = view || "Rating";

    const cats = timeData.map(item => item.monthYear);
    let posData = [];
    let negData = [];

    if (currentEffectiveView === "Sales") {
      posData = timeData.map(item => item.positiveRevenue);
      negData = timeData.map(item => item.negativeRevenue);
    } else {
      posData = timeData.map(item => item.averagePositiveRating);
      negData = timeData.map(item => item.averageNegativeRating);
    }
    
    return { categories: cats, positiveData: posData, negativeData: negData, effectiveView: currentEffectiveView };

  }, [aggregatedData, isAggregatedLoading, view]);

  const onAxisClick = (event, params) => {
     if (params && params.dataIndex !== undefined) {
       const clickedMonthString = categories[params.dataIndex];
       try {
         const dateObject = new Date(clickedMonthString + " 1");
         if (!isNaN(dateObject.getTime())) {
           setSelectedDate(dateObject); 
         }
       } catch (e) {
         setSelectedDate(null);
       }
     }
  };

  let title = effectiveView === "Sales" ? "Revenue Impact Trends" : "Rating Impact Trends";

  if (selectedCategory || selectedSubject || selectedAttribute) {
    title += " for";
    if (selectedCategory) title += ` ${selectedCategory}`;
    if (selectedSubject) title += `${selectedCategory ? "," : ""} ${selectedSubject}`;
    if (selectedAttribute) title += `${(selectedCategory || selectedSubject) ? "," : ""} ${selectedAttribute}`;
  }

  const chartSlotProps = useMemo(() => ({
      ...responsiveSlotProps,
      legend: { hidden: true },
      tooltip: { content: (props) => <CustomChartTooltip {...props} currencySymbol={currencySymbol} view={effectiveView} /> } 
  }), [responsiveSlotProps, currencySymbol, effectiveView]);

  const isLoading = isAggregatedLoading || (!aggregatedData && (isReviewsLoading || isAnalysisLoading));

  return (
    <StandardChartCard 
      data-chart-type="timeline-chart"
      data-filter-exempt="true"
      title={title} 
      titleIcon={<TrendingUpIcon />}
    >
    {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      ) : categories.length === 0 ? (
         <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
           <Typography variant="body2" color="textSecondary">No trend data found for the selected filters.</Typography>
         </Box>
      ) : (
        <BarChart
          sx={chartSx}
          onAxisClick={onAxisClick}
          xAxis={[getXAxisConfig(categories, isSmallScreen)]}
          yAxis={[getYAxisConfig(currencySymbol, effectiveView, isSmallScreen)]}
          series={[
            {
              label: 'Positive',
              data: positiveData,
              stack: 'impact',
              stackOffset: 'diverging',
              color: colors.positiveGreen,
              valueFormatter: (value) => formatChartValue(value, currencySymbol, effectiveView),
            },
            {
              label: 'Negative',
              data: negativeData,
              stack: 'impact',
              color: colors.negativeRed,
              valueFormatter: (value) => formatChartValue(value, currencySymbol, effectiveView),
            },
          ]}
          slotProps={chartSlotProps}
          margin={margin}
        />
      )}
    </StandardChartCard>
  );
};

export default ImpactOverTimeChart;
